import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  Activity, 
  Database,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Server,
  Globe,
  Shield
} from 'lucide-react';

const SystemOverview = () => {
  const [stats, setStats] = useState({
    tenants: [],
    totalUsers: 0,
    totalTenants: 0,
    systemHealth: 'unknown'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadSystemStats();
    }
  }, [user]);

  const loadSystemStats = async () => {
    try {
      setLoading(true);
      
      // Load tenants using axios (which has auth headers configured)
      const repoResponse = await axios.get('/api/tenants');
      const tenants = repoResponse.data.tenants || [];

      // Load users - use the correct endpoint for admin users
      let totalUsers = 0;
      try {
        const userResponse = await axios.get('/api/auth/users');
        totalUsers = userResponse.data.users?.length || userResponse.data.total || 0;
      } catch (userError) {
        console.log('Could not load user count:', userError);
      }

      // Check system health
      const healthResponse = await fetch('/health');
      const systemHealth = healthResponse.ok ? 'healthy' : 'degraded';

      setStats({
        tenants,
        totalUsers,
        totalTenants: tenants.length,
        systemHealth
      });

    } catch (error) {
      console.error('Error loading system stats:', error);
      setError('Failed to load system statistics');
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatus = () => {
    switch (stats.systemHealth) {
      case 'healthy':
        return { icon: '✅', text: 'Healthy', class: 'healthy' };
      case 'degraded':
        return { icon: '⚠️', text: 'Degraded', class: 'degraded' };
      default:
        return { icon: '❓', text: 'Unknown', class: 'unknown' };
    }
  };

  const healthStatus = getHealthStatus();

  if (loading) {
    return (
      <div className="loading-container-modern flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="loading-spinner-modern w-12 h-12 border-4 border-blue-200 dark:border-blue-900 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading system overview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container-modern bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8 max-w-2xl mx-auto mt-8">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-600 dark:text-red-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">Error Loading System Stats</h3>
          <p className="text-red-700 dark:text-red-300 mb-4">{error}</p>
          <button 
            onClick={loadSystemStats}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-all duration-200 font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="system-overview-modern">
      {/* Header Section */}
      <div className="overview-header-modern mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">System Overview</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Multi-Tenant CRM System Status & Analytics</p>
          </div>
          <button 
            className="refresh-btn bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 font-medium shadow-sm hover:shadow"
            onClick={() => loadSystemStats()}
          >
            <RefreshCw size={16} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="stats-grid-modern grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        <div className="stat-card-modern bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="stat-icon-modern bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 p-3 rounded-lg">
              <Building2 size={24} />
            </div>
            <TrendingUp className="text-green-500" size={20} />
          </div>
          <div className="stat-content-modern">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{stats.totalTenants}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Active Tenants</p>
          </div>
        </div>

        <div className="stat-card-modern bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="stat-icon-modern bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 p-3 rounded-lg">
              <Users size={24} />
            </div>
            <TrendingUp className="text-green-500" size={20} />
          </div>
          <div className="stat-content-modern">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{stats.totalUsers}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
          </div>
        </div>

        <div className="stat-card-modern bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="stat-icon-modern bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 p-3 rounded-lg">
              <Database size={24} />
            </div>
            {stats.systemHealth === 'healthy' ? (
              <CheckCircle2 className="text-green-500" size={20} />
            ) : (
              <AlertCircle className="text-yellow-500" size={20} />
            )}
          </div>
          <div className="stat-content-modern">
            <h3 className={`text-3xl font-bold mb-1 ${
              stats.systemHealth === 'healthy' ? 'text-green-600 dark:text-green-400' :
              stats.systemHealth === 'degraded' ? 'text-yellow-600 dark:text-yellow-400' :
              'text-gray-600 dark:text-gray-400'
            }`}>
              {healthStatus.text}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">System Health</p>
          </div>
        </div>

        <div className="stat-card-modern bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="stat-icon-modern bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 p-3 rounded-lg">
              <Activity size={24} />
            </div>
            <Activity className="text-blue-500" size={20} />
          </div>
          <div className="stat-content-modern">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Active</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">System Status</p>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        {/* Tenants Overview - Takes 2 columns */}
        <div className="xl:col-span-2">
          <div className="tenants-overview-modern bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <Building2 className="mr-2" size={20} />
                Tenant Summary
              </h3>
              <span className="text-sm text-gray-600 dark:text-gray-400">{stats.tenants.length} total</span>
            </div>
            {stats.tenants.length === 0 ? (
              <div className="no-tenants-modern text-center py-12">
                <Building2 className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={48} />
                <p className="text-gray-600 dark:text-gray-400">No tenants found. Create your first tenant to get started.</p>
              </div>
            ) : (
              <div className="tenant-summary-grid-modern grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto pr-2">
                {stats.tenants.map((tenant) => (
                  <div key={tenant.id} className="tenant-summary-card-modern bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{tenant.name}</h4>
                        <span className="tenant-code-modern inline-block text-xs text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded mt-1">{tenant.code}</span>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`access-badge-modern text-xs font-medium px-2 py-0.5 rounded ${
                          tenant.accessLevel.toLowerCase() === 'admin' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' : 
                          tenant.accessLevel.toLowerCase() === 'write' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 
                          'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}>
                          {tenant.accessLevel}
                        </span>
                        {tenant.isDefault && (
                          <span className="default-badge-modern bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 text-xs font-medium px-2 py-0.5 rounded">⭐ Default</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                      {tenant.description || 'No description available'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* System Information - Takes 1 column */}
        <div className="xl:col-span-1">
          <div className="system-info-modern bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 h-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
              <Server className="mr-2" size={20} />
              System Information
            </h3>
            <div className="space-y-4">
              <div className="info-item-modern">
                <div className="flex items-center space-x-2 mb-1">
                  <Shield size={16} className="text-blue-500" />
                  <span className="info-label-modern text-sm font-medium text-gray-700 dark:text-gray-300">Authentication</span>
                </div>
                <p className="info-value-modern text-sm text-gray-600 dark:text-gray-400 ml-6">Dual (Admin SQLite + Tenant DB)</p>
              </div>
              
              <div className="info-item-modern">
                <div className="flex items-center space-x-2 mb-1">
                  <Globe size={16} className="text-green-500" />
                  <span className="info-label-modern text-sm font-medium text-gray-700 dark:text-gray-300">Multi-Tenant</span>
                </div>
                <p className="info-value-modern text-sm text-gray-600 dark:text-gray-400 ml-6">Enabled</p>
              </div>
              
              <div className="info-item-modern">
                <div className="flex items-center space-x-2 mb-1">
                  <Database size={16} className="text-purple-500" />
                  <span className="info-label-modern text-sm font-medium text-gray-700 dark:text-gray-300">Database Types</span>
                </div>
                <p className="info-value-modern text-sm text-gray-600 dark:text-gray-400 ml-6">SQL Server + PostgreSQL + SQLite</p>
              </div>
              
              <div className="info-item-modern">
                <div className="flex items-center space-x-2 mb-1">
                  <Users size={16} className="text-amber-500" />
                  <span className="info-label-modern text-sm font-medium text-gray-700 dark:text-gray-300">Registration</span>
                </div>
                <p className="info-value-modern text-sm text-gray-600 dark:text-gray-400 ml-6">Email Domain Auto-assign</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="quick-actions-modern bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Activity className="mr-2" size={20} />
          Quick Actions
        </h3>
        <div className="action-buttons-modern flex flex-wrap gap-3">
          <button 
            className="action-btn-modern bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 font-medium shadow-sm hover:shadow"
            onClick={() => loadSystemStats()}
          >
            <RefreshCw size={16} />
            <span>Refresh Stats</span>
          </button>
          <button 
            className="action-btn-modern bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 font-medium shadow-sm hover:shadow"
            onClick={() => window.open('/health', '_blank')}
          >
            <Activity size={16} />
            <span>Health Check</span>
          </button>
          <a 
            href="/admin?tab=database"
            className="action-btn-modern bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 font-medium shadow-sm hover:shadow no-underline"
          >
            <Database size={16} />
            <span>Database Validation</span>
          </a>
          <a 
            href="/admin?tab=tenants"
            className="action-btn-modern bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 font-medium shadow-sm hover:shadow no-underline"
          >
            <Building2 size={16} />
            <span>Manage Tenants</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default SystemOverview;